import { Form, Formik } from 'formik';
import { PaymentStepProps } from './MakePayment';
import FormTextField from '../../../../components/form/FormTextField';
import { FileUpload } from '../../../../components/form/FileUpload/FileUpload';
import Button from '../../../../components/button/Button';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

type Props = PaymentStepProps & {
    initialValues: Partial<MoreDetailsForm>;
};

export type MoreDetailsForm = {
    attachments: File[];
    notes: string;
};

export const MoreDetails: React.FC<Props> = ({ initialValues, nextStep }) => {
    const { t } = useTranslation('transactions');
    const [notesLengthMessage, setNotesLengthMessage] = useState(
        t('maxCharacters')
    );

    const onSubmit = async (values: MoreDetailsForm) => {
        nextStep(values);
    };

    useEffect(() => {
        if (initialValues?.notes?.length !== undefined && initialValues?.notes?.length > 0) {
            setNotesLengthMessage(t('charactersRemaining', { count: 1000 - initialValues.notes.length }));
        } else {
            setNotesLengthMessage(t('maxCharacters'));
        }
    }, [initialValues?.notes, t]);

    const handleNotesChange = useCallback(
        (e: { target: { value: any } }, setFieldValue: (name: string, value: any) => void) => {
            const value = e.target.value;
            if (value.length > 0) {
                setNotesLengthMessage(t('charactersRemaining', { count: 1000 - value.length }));
            } else {
                setNotesLengthMessage(t('maxCharacters'));
            }
            setFieldValue('notes', value);
        },
        [t]
    );

    const validationSchema = Yup.object({
        notes: Yup.string()
        .notRequired()
        .test(
            'min-if-filled',
            t('noteMinChars'),
            (value) => !value || value.trim().length >= 5
        ),
    });

    return (
        <Formik
            initialValues={{
                attachments: [],
                notes: '',
                ...initialValues,
            }}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            {({ setFieldValue }) => (
                <Form>
                    <p className="mb-4">{t('moreInfoPayment')}</p>
                    <FileUpload
                        label={t('attachments')}
                        fieldName="attachments"
                        emptyUploadTitle={t('dragAndDrop')}
                        emptyUploadMessage={t('acceptedFormats')}
                        multi
                        small
                    />
                    <FormTextField
                        label={t('notes')}
                        field="notes"
                        type="textarea"
                        placeholder={t('writeTextHere')}
                        maxLength={1000}
                        inputProps={{ rows: undefined }}
                        hint={notesLengthMessage}
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleNotesChange(e, setFieldValue);
                        }}
                    />
                    <Button type="submit" color="third">
                        {t('next')}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
